import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/en-gb';
import uuid from 'react-uuid';
import { tokens } from '../../../global/theme/tokens';
import TypeMoney from './TypeMoney';
import TypeNumber from './TypeNumber';
import { parseDecimal } from './TypeTravelExpenseLegs';
import { DBUploadData, useConfig } from '../../../api/api';
import { ExoFilePreview } from '../../exo/ExoFilePreview';
import { numberToString } from '../../special/numberConverter';
import { round } from '../../special/round';

export const travelExpenseCostCategories = [
  { label: 'Parking', value: 'parking' },
  { label: 'Toll', value: 'toll' },
  { label: 'Hotel', value: 'hotel' },
  { label: 'Public Transport', value: 'public-transport' },
  { label: 'Taxi', value: 'taxi' },
  { label: 'Fuel', value: 'fuel' },
  { label: 'Other', value: 'other' },
];

// The files endpoint takes the FileTypeEnum CASE NAME, not its numeric value.
const RECEIPT_FILE_TYPE = 'RECEIPT';

export function emptyCost() {
  return {
    uuid: uuid(),
    category: 'parking',
    // Defaults to today so the row is priced the moment an amount is typed;
    // most receipts are entered the day they happen anyway.
    incurredOn: moment().format('YYYY-MM-DD'),
    description: '',
    grossAmount: '',
    taxRate: 19,
    fileId: null,
    fileName: '',
    file: null,
  };
}

/** New rows carry a uuid, rows loaded from the server an id. */
function costKey(cost) {
  return cost.uuid ?? cost.id;
}

/** Shown while typing. The stored split is recomputed by the server on save. */
function netPreview(cost) {
  const gross = Number(cost.grossAmount) || 0;
  const taxRate = parseDecimal(cost.taxRate) || 0;
  return round(gross / (1 + taxRate / 100), 2);
}

/**
 * Additional costs (Nebenkosten): real receipts with a VAT split, one card per
 * cost. Rows wrap on their own widths so the same markup works in a desktop
 * column and on a phone.
 *
 * A receipt file is uploaded on selection and only its id travels with the form.
 * The uploaded receipt shows as a chip with the filename, a preview and a
 * remove action, and a failed upload says so instead of silently doing nothing.
 */
const TypeTravelExpenseCosts = ({
  field,
  dataCollection,
  errors = {},
  updateValidationOnChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const dateFormat = config?.general?.dateOutputFormat || 'DD.MM.YYYY';

  // "en-gb" uses Monday as the first day of the week (same as TypeDate).
  useEffect(() => {
    moment.locale('en-gb');
  }, []);

  const costs = dataCollection[field.key] || [];
  const currencySymbol = field.currencySymbol ?? '';
  const error = errors[field.key];

  const [uploadingKey, setUploadingKey] = useState(null);
  const [uploadErrors, setUploadErrors] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const inputRefs = useRef({});

  // Always the costs as currently rendered, for callbacks that outlive a
  // render (the upload response).
  const costsRef = useRef(costs);
  costsRef.current = costs;

  function commit(newCosts) {
    updateValidationOnChange(field.key, newCosts);
  }

  function handleChange(index, key, value) {
    const newCosts = [...costs];
    newCosts[index] = { ...newCosts[index], [key]: value };
    commit(newCosts);
  }

  function handleAdd() {
    commit([...costs, emptyCost()]);
  }

  function handleDelete(index) {
    commit(costs.filter((_, i) => i !== index));
  }

  function handleFile(cost, file) {
    if (!file) return;
    const key = costKey(cost);

    const formData = new FormData();
    formData.append('files[]', file);
    formData.append('fileType', RECEIPT_FILE_TYPE);

    setUploadingKey(key);
    setUploadErrors((prev) => ({ ...prev, [key]: null }));

    DBUploadData({
      config,
      path: 'files',
      formData,
      onResponse: (response) => {
        const uploaded = response?.data?.data?.[0];
        setUploadingKey((prev) => (prev === key ? null : prev));

        if (!uploaded) {
          setUploadErrors((prev) => ({ ...prev, [key]: t('Upload failed') }));
          return;
        }

        // Merged into the costs as they are NOW, addressed by the row's key:
        // other rows may have been edited or removed while the file was in
        // flight, and a deleted row must stay deleted.
        const currentCosts = costsRef.current;
        if (!currentCosts.some((current) => costKey(current) === key)) return;
        commit(
          currentCosts.map((current) =>
            costKey(current) === key
              ? {
                  ...current,
                  fileId: uploaded.id,
                  fileName: uploaded.title || file.name,
                  // Kept for the preview; only fileId is submitted.
                  file: uploaded,
                }
              : current,
          ),
        );
      },
      onError: () => {
        setUploadingKey((prev) => (prev === key ? null : prev));
        setUploadErrors((prev) => ({ ...prev, [key]: t('Upload failed') }));
      },
    });
  }

  function handleRemoveFile(index) {
    const newCosts = [...costs];
    const key = costKey(newCosts[index]);
    newCosts[index] = { ...newCosts[index], fileId: null, fileName: '', file: null };
    commit(newCosts);
    if (inputRefs.current[key]) inputRefs.current[key].value = null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box
        sx={{
          gridColumn: field.colspan
            ? `span ${field.colspan} / span ${field.colspan}`
            : 'auto',
        }}
        className="flex flex-col gap-2 w-full"
      >
        {costs.length === 0 && (
          <Typography variant="body2" className="opacity-60 px-1">
            {t('No additional costs. Add parking, hotel or ticket receipts here.')}
          </Typography>
        )}

        {costs.map((cost, index) => (
          <Box
            key={cost.uuid || cost.id}
            className="flex flex-col gap-2 p-3 rounded-lg"
            sx={{
              backgroundColor: colors.bgInput,
              border: `1px solid ${colors.selected}`,
            }}
          >
            <Box className="flex flex-wrap items-center gap-2">
              <TextField
                select
                size="small"
                label={t('Category')}
                value={cost.category || 'other'}
                onChange={(e) => handleChange(index, 'category', e.target.value)}
                sx={{ flexGrow: 1, minWidth: '140px', flexBasis: '140px' }}
              >
                {travelExpenseCostCategories.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </TextField>

              <DatePicker
                label={t('Date')}
                format={dateFormat}
                value={cost.incurredOn ? moment(cost.incurredOn) : null}
                onChange={(newValue) =>
                  handleChange(
                    index,
                    'incurredOn',
                    newValue && newValue.isValid()
                      ? newValue.format('YYYY-MM-DD')
                      : '',
                  )
                }
                slotProps={{ textField: { size: 'small' } }}
                sx={{ width: '165px', flexShrink: 0 }}
              />

              <Tooltip title={t('Delete')}>
                <IconButton
                  size="small"
                  className="ml-auto"
                  onClick={() => handleDelete(index)}
                  sx={{ ':hover': { color: colors.error } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Box className="flex flex-wrap items-center gap-2">
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: '110px',
                  flexBasis: '110px',
                  '& .MuiFormControl-root': { width: '100%' },
                }}
              >
                <TypeMoney
                  size="small"
                  field={{ label: t('Gross amount'), key: 'grossAmount' }}
                  dataCollection={cost}
                  updateValidationOnChange={(key, value) =>
                    handleChange(index, 'grossAmount', value)
                  }
                />
              </Box>
              <Box sx={{ width: '100px', flexShrink: 0 }}>
                <TypeNumber
                  size="small"
                  field={{ label: t('Tax rate'), key: 'taxRate', unit: '%' }}
                  dataCollection={cost}
                  updateValidationOnChange={(key, value) =>
                    handleChange(index, 'taxRate', value)
                  }
                />
              </Box>

              <Typography
                variant="body2"
                className="whitespace-nowrap opacity-60 monospace ml-auto"
              >
                {`${t('Net amount')}: ${numberToString(netPreview(cost))} ${currencySymbol}`}
              </Typography>
            </Box>

            <Box className="flex flex-wrap items-center gap-2">
              <TextField
                size="small"
                label={t('Description')}
                value={cost.description || ''}
                onChange={(e) => handleChange(index, 'description', e.target.value)}
                sx={{ flexGrow: 1, minWidth: '150px', flexBasis: '150px' }}
              />

              {cost.fileId ? (
                <Box
                  className="flex items-center gap-1 rounded-md pl-2 pr-1 py-0.5"
                  sx={{
                    border: `1px solid ${colors.selected}`,
                    backgroundColor: colors.card,
                  }}
                >
                  <ReceiptLongIcon fontSize="small" className="opacity-60" />
                  <Tooltip title={cost.fileName || t('Receipt')}>
                    <Typography
                      variant="body2"
                      className="truncate"
                      sx={{ maxWidth: '140px' }}
                    >
                      {cost.fileName || t('Receipt')}
                    </Typography>
                  </Tooltip>
                  {cost.file?.temporaryUrl && (
                    <Tooltip title={t('Preview')}>
                      <IconButton
                        size="small"
                        onClick={() => setPreviewFile(cost.file)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t('Remove receipt')}>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      sx={{ ':hover': { color: colors.error } }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                // No receipt yet: the button doubles as the warning, so the
                // gap is visible right where it can be fixed.
                <Tooltip title={t('Receipt missing')}>
                  <Button
                    size="small"
                    color="warning"
                    startIcon={<UploadFileIcon />}
                    disabled={uploadingKey === costKey(cost)}
                    onClick={() => inputRefs.current[costKey(cost)]?.click()}
                    sx={{
                      border: `1px dashed ${colors.warning}`,
                      backgroundColor: colors.warning + '10',
                    }}
                  >
                    {uploadingKey === costKey(cost)
                      ? t('Uploading...')
                      : t('Add receipt')}
                  </Button>
                </Tooltip>
              )}

              <input
                ref={(element) => {
                  inputRefs.current[costKey(cost)] = element;
                }}
                type="file"
                hidden
                onChange={(e) => handleFile(cost, e.target.files[0])}
              />
            </Box>

            {uploadErrors[costKey(cost)] && (
              <Typography variant="body2" sx={{ color: colors.error }}>
                {uploadErrors[costKey(cost)]}
              </Typography>
            )}
          </Box>
        ))}

        {error && (
          <Typography variant="body2" sx={{ color: colors.error }}>
            {error}
          </Typography>
        )}

        <Box>
          <Button size="small" startIcon={<AddCircleIcon />} onClick={handleAdd}>
            {t('Add cost')}
          </Button>
        </Box>

        <ExoFilePreview
          open={Boolean(previewFile)}
          src={previewFile?.temporaryUrl}
          mimeType={previewFile?.mimeType || 'application/pdf'}
          onClose={() => setPreviewFile(null)}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default TypeTravelExpenseCosts;
